// components/updated-ui/VoiceChat.js
import React, { useState, useEffect, useRef } from "react";
import { Button, Box, Typography, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

// STUN servers for discovering network addresses
const iceServers = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

const VoiceChat = ({ socket, roomCode, players }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isInVoiceChat, setIsInVoiceChat] = useState(false);
  const localAudioStreamRef = useRef(null);
  const peerConnectionsRef = useRef({}); // Stores all peer connections { socketId: RTCPeerConnection }
  const audioElementsRef = useRef({}); // Stores all <audio> elements { socketId: HTMLAudioElement }

  useEffect(() => {
    if (!socket || !isInVoiceChat) return;

    // Handler for when another user joins the voice chat
    const handleUserJoined = ({ userId }) => {
      console.log("New user joined voice:", userId);
      // Create an offer to connect to the new user
      createPeerConnection(userId, true);
    };

    // Handler for receiving a WebRTC offer
    const handleOffer = ({ from, offer }) => {
      console.log("Received offer from:", from);
      const peerConnection = createPeerConnection(from, false);
      peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      peerConnection.createAnswer().then((answer) => {
        peerConnection.setLocalDescription(answer);
        socket.emit("webrtc-answer", { to: from, answer });
      });
    };

    // Handler for receiving a WebRTC answer
    const handleAnswer = ({ from, answer }) => {
      console.log("Received answer from:", from);
      const peerConnection = peerConnectionsRef.current[from];
      if (peerConnection) {
        peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      }
    };

    // Handler for receiving ICE candidates
    const handleIceCandidate = ({ from, candidate }) => {
      const peerConnection = peerConnectionsRef.current[from];
      if (peerConnection) {
        peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      }
    };

    const handleUserLeft = ({ userId }) => {
      console.log("User left voice:", userId);
      if (peerConnectionsRef.current[userId]) {
        peerConnectionsRef.current[userId].close();
        delete peerConnectionsRef.current[userId];
      }
      if (audioElementsRef.current[userId]) {
        audioElementsRef.current[userId].remove();
        delete audioElementsRef.current[userId];
      }
    };

    socket.on("user-joined-voice", handleUserJoined);
    socket.on("webrtc-offer", handleOffer);
    socket.on("webrtc-answer", handleAnswer);
    socket.on("webrtc-ice-candidate", handleIceCandidate);
    socket.on("user-left-voice", handleUserLeft);

    return () => {
      socket.off("user-joined-voice", handleUserJoined);
      socket.off("webrtc-offer", handleOffer);
      socket.off("webrtc-answer", handleAnswer);
      socket.off("webrtc-ice-candidate", handleIceCandidate);
      socket.off("user-left-voice", handleUserLeft);
    };
  }, [socket, isInVoiceChat]);

  const createPeerConnection = (remoteUserId, isInitiator) => {
    if (peerConnectionsRef.current[remoteUserId]) {
      return peerConnectionsRef.current[remoteUserId];
    }
    const peerConnection = new RTCPeerConnection(iceServers);
    peerConnectionsRef.current[remoteUserId] = peerConnection;

    // Add local audio stream to the connection
    localAudioStreamRef.current.getTracks().forEach((track) => {
      peerConnection.addTrack(track, localAudioStreamRef.current);
    });

    // Handle incoming remote audio stream
    peerConnection.ontrack = (event) => {
      const remoteStream = event.streams[0];
      let audio = audioElementsRef.current[remoteUserId];
      if (!audio) {
        audio = document.createElement("audio");
        audio.autoplay = true;
        audioElementsRef.current[remoteUserId] = audio;
        document.body.appendChild(audio); // Append hidden audio element
      }
      audio.srcObject = remoteStream;
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit("webrtc-ice-candidate", {
          to: remoteUserId,
          candidate: event.candidate,
        });
      }
    };

    // If this client is the one initiating the call
    if (isInitiator) {
      peerConnection.createOffer().then((offer) => {
        peerConnection.setLocalDescription(offer);
        socket.emit("webrtc-offer", { to: remoteUserId, offer });
      });
    }

    return peerConnection;
  };

  const joinChat = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localAudioStreamRef.current = stream;
      stream.getAudioTracks()[0].enabled = false; // Start muted by default
      setIsInVoiceChat(true);
      socket.emit("join-voice", { roomCode });
    } catch (error) {
      console.error("Could not get microphone access:", error);
      alert("Microphone access is required for voice chat.");
    }
  };

  const leaveChat = () => {
    socket.emit("leave-voice", { roomCode });
    // Stop local microphone track
    if (localAudioStreamRef.current) {
      localAudioStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    // Close all peer connections
    Object.values(peerConnectionsRef.current).forEach((pc) => pc.close());
    // Remove all audio elements
    Object.values(audioElementsRef.current).forEach((audio) => audio.remove());

    peerConnectionsRef.current = {};
    audioElementsRef.current = {};
    localAudioStreamRef.current = null;
    setIsInVoiceChat(false);
  };

  const toggleMute = () => {
    if (localAudioStreamRef.current) {
      const audioTrack = localAudioStreamRef.current.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Typography variant="subtitle2">Voice Chat</Typography>
      {!isInVoiceChat ? (
        <Button variant="contained" onClick={joinChat}>
          Join Voice
        </Button>
      ) : (
        // <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <>
          <Tooltip title={isMuted ? "Unmute" : "Mute"}>
            <Button
              variant="contained"
              color={isMuted ? "secondary" : "primary"}
              onClick={toggleMute}
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </Button>
          </Tooltip>
          <Button variant="outlined" color="error" onClick={leaveChat}>
            Leave
          </Button>
        </>
        // </Box>
      )}
    </Box>
  );
};

export default VoiceChat;
